import { useEffect, useRef, useState } from "react";

/**
 * useAutoFrame — Center-Stage style auto-framing for a <video> element.
 *
 * Tracks the dominant face in the video stream (via the browser's
 * FaceDetector API on Chromium / Android Chrome) and returns smoothed
 * X/Y percentages a parent component can plug into `object-position` so
 * the framing follows the host as they move around. Falls back to a
 * neutral 50/50 centre when FaceDetector is missing (Safari/Firefox) so
 * the toggle still behaves like a "lock to center" preset.
 *
 * No track replacement — this is a *preview-only* recompose using CSS
 * `object-position`. The outgoing WebRTC track is untouched, which means
 * we don't add encoder load and remote viewers see the full sensor frame
 * (ie. the broadcaster looks pro on their own preview but doesn't crop
 * what they share).
 *
 * @param {React.RefObject<HTMLVideoElement>} videoRef
 * @param {boolean} enabled
 * @returns {{ x: number, y: number, hasFace: boolean, supported: boolean }}
 */
export default function useAutoFrame(videoRef, enabled) {
  const [pos, setPos] = useState({ x: 50, y: 50, hasFace: false });
  const supportedRef = useRef(typeof window !== "undefined" && "FaceDetector" in window);

  useEffect(() => {
    if (!enabled) {
      setPos({ x: 50, y: 50, hasFace: false });
      return undefined;
    }
    let cancelled = false;
    let detector = null;
    try {
      // eslint-disable-next-line no-undef
      detector = supportedRef.current ? new FaceDetector({ fastMode: true, maxDetectedFaces: 1 }) : null;
    } catch {
      detector = null;
    }

    const canvas = document.createElement("canvas");
    canvas.width = 256;
    canvas.height = 144;
    const ctx = canvas.getContext("2d");

    // Smoothing — don't snap, lerp toward the new face centroid so the
    // framing feels filmic rather than jittery.
    let smoothX = 50;
    let smoothY = 50;

    const tick = async () => {
      if (cancelled) return;
      const v = videoRef.current;
      if (!v || v.readyState < 2 || v.videoWidth === 0) {
        return scheduleNext();
      }
      try {
        ctx.drawImage(v, 0, 0, canvas.width, canvas.height);
      } catch {
        return scheduleNext();
      }
      let target = { x: 50, y: 50, hasFace: false };
      if (detector) {
        try {
          const faces = await detector.detect(canvas);
          if (faces && faces.length > 0) {
            const f = faces[0].boundingBox;
            const cx = (f.x + f.width / 2) / canvas.width;
            const cy = (f.y + f.height / 2) / canvas.height;
            target = {
              x: Math.min(95, Math.max(5, cx * 100)),
              y: Math.min(95, Math.max(5, cy * 100)),
              hasFace: true,
            };
          }
        } catch {
          // FaceDetector can throw "InvalidStateError" mid-frame; ignore
        }
      }
      smoothX = smoothX + (target.x - smoothX) * 0.18;
      smoothY = smoothY + (target.y - smoothY) * 0.18;
      if (!cancelled) {
        setPos({
          x: Math.round(smoothX * 10) / 10,
          y: Math.round(smoothY * 10) / 10,
          hasFace: target.hasFace,
        });
      }
      scheduleNext();
    };

    let timer = null;
    const scheduleNext = () => {
      if (cancelled) return;
      timer = window.setTimeout(tick, 350);
    };

    tick();
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [videoRef, enabled]);

  return { ...pos, supported: supportedRef.current };
}
